import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { Search, X, ChevronLeft, ChevronRight, Filter } from 'lucide-react'
import CinematicNav, { BackToTop } from '../../components/landing/CinematicNav'
import CinematicFooter from '../../components/landing/CinematicFooter'
import Reveal from '../../components/landing/Reveal'
import Breadcrumbs from '../../components/masterclass/Breadcrumbs'
import { resolveImageUrl } from '../../utils/imageUrl'
import { SCHEDULE_SERIES_NAME } from '../../utils/masterclassSchedule'
import { masterclassService } from '../../services/masterclassService'

function Lightbox({ images, startIndex, onClose }) {
  const [index, setIndex] = useState(startIndex)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowRight') setIndex((i) => (i + 1) % images.length)
      if (e.key === 'ArrowLeft') setIndex((i) => (i - 1 + images.length) % images.length)
    }
    window.addEventListener('keydown', handleKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handleKey)
      document.body.style.overflow = ''
    }
  }, [images.length, onClose])

  return (
    <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center" onClick={onClose}>
      <button className="absolute top-6 right-6 text-white/70 hover:text-white z-50" onClick={onClose}>
        <X className="w-8 h-8" />
      </button>
      {images.length > 1 && (
        <>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i - 1 + images.length) % images.length) }}>
            <ChevronLeft className="w-10 h-10" />
          </button>
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); setIndex((i) => (i + 1) % images.length) }}>
            <ChevronRight className="w-10 h-10" />
          </button>
        </>
      )}
      <img
        src={resolveImageUrl(images[index].image_url)}
        alt={images[index].caption || ''}
        loading="lazy"
        className="max-h-[90vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
      />
      {images[index].caption && (
        <div className="absolute bottom-6 left-0 right-0 text-center text-white/80 font-sans text-sm px-4">
          {images[index].caption}
        </div>
      )}
    </div>
  )
}

export default function GalleryPage() {
  const [images, setImages] = useState([])
  const [speakers, setSpeakers] = useState([])
  const [years, setYears] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState({ speaker: '', year: '', category: '' })
  const [showFilters, setShowFilters] = useState(false)
  const [lightbox, setLightbox] = useState(null)
  const [scrolled, setScrolled] = useState(false)
  const [showBackToTop, setShowBackToTop] = useState(false)
  const gridRef = useRef(null)

  useEffect(() => {
    document.title = `Masterclass Gallery | ${SCHEDULE_SERIES_NAME}`
    const handleScroll = () => {
      setScrolled(window.scrollY > 40)
      setShowBackToTop(window.scrollY > 800)
    }
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setLoading(true)
    Promise.all([
      masterclassService.getGallery({
        speaker_id: filters.speaker,
        year: filters.year,
        category: filters.category,
        search,
        limit: 200,
      }),
      masterclassService.getSpeakers(),
      masterclassService.getYears(),
      masterclassService.getCategories(),
    ]).then(([gallery, speakersRes, yearsRes, catsRes]) => {
      setImages(gallery.images || [])
      setSpeakers(speakersRes.speakers || [])
      setYears(yearsRes.years || [])
      setCategories(catsRes.categories || [])
    }).catch(() => {
      setImages([])
      setSpeakers([])
      setYears([])
      setCategories([])
    }).finally(() => setLoading(false))
  }, [filters, search])

  return (
    <div className="cinema-page min-h-screen bg-cinema-black">
      <div className="cinema-grain" aria-hidden="true" />
      <CinematicNav scrolled={scrolled} navOverHero={!scrolled} />

      {/* Hero */}
      <section className="relative pt-40 pb-24 overflow-hidden">
        <div className="cinema-glow-orb w-[700px] h-[700px] -top-40 left-1/2 -translate-x-1/2 opacity-30" />
        <div className="cinema-container relative">
          <Reveal>
            <Breadcrumbs />
          </Reveal>
          <Reveal delay={100}>
            <span className="cinema-label mt-10 block">Visual Archive</span>
            <h1 className="cinema-headline-lg mt-6">
              Masterclass <span className="text-cinema-gold">Gallery</span>
            </h1>
            <p className="cinema-body-lg mt-6 max-w-2xl text-cinema-muted">
              Moments from our creative talk series, captured for the community.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-0 z-30 bg-cinema-black/90 backdrop-blur-md border-b border-white/5 py-4">
        <div className="cinema-container">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cinema-muted" />
              <input
                type="text"
                placeholder="Search captions, speakers, topics..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-cinema-graphite border-b border-white/10 text-white pl-10 pr-4 py-2.5 text-sm font-sans focus:outline-none focus:border-cinema-gold/40 transition-colors"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2.5 text-cinema-muted text-sm font-sans hover:text-cinema-gold transition-colors"
            >
              <Filter className="w-4 h-4" /> Filters
            </button>
          </div>

          {showFilters && (
            <div className="grid sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-white/5">
              <div>
                <label className="text-cinema-muted text-xs font-sans uppercase mb-2 block">Speaker</label>
                <select
                  value={filters.speaker}
                  onChange={(e) => setFilters(f => ({ ...f, speaker: e.target.value }))}
                  className="w-full bg-cinema-graphite border-b border-white/10 text-white px-3 py-2 text-sm font-sans focus:outline-none focus:border-cinema-gold/40"
                >
                  <option value="">All Speakers</option>
                  {speakers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-cinema-muted text-xs font-sans uppercase mb-2 block">Year</label>
                <select
                  value={filters.year}
                  onChange={(e) => setFilters(f => ({ ...f, year: e.target.value }))}
                  className="w-full bg-cinema-graphite border-b border-white/10 text-white px-3 py-2 text-sm font-sans focus:outline-none focus:border-cinema-gold/40"
                >
                  <option value="">All Years</option>
                  {years.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-cinema-muted text-xs font-sans uppercase mb-2 block">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters(f => ({ ...f, category: e.target.value }))}
                  className="w-full bg-cinema-graphite border-b border-white/10 text-white px-3 py-2 text-sm font-sans focus:outline-none focus:border-cinema-gold/40"
                >
                  <option value="">All Categories</option>
                  {categories.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section ref={gridRef} className="bg-cinema-charcoal py-24 sm:py-32">
        <div className="cinema-container">
          {loading ? (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
              {[...Array(9)].map((_, i) => (
                <div key={i} className="bg-cinema-graphite/50 aspect-square animate-pulse" />
              ))}
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-24">
              <div className="text-6xl mb-6">📸</div>
              <h3 className="font-display text-2xl text-white">No photos yet</h3>
              <p className="text-cinema-muted font-sans mt-3">Photos from upcoming sessions will appear here.</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
              {images.map((img, i) => (
                <Reveal key={img.id} delay={i * 40}>
                  <button
                    onClick={() => setLightbox(i)}
                    className="block w-full text-left overflow-hidden group relative"
                  >
                    <img
                      src={resolveImageUrl(img.image_url)}
                      alt={img.caption || ''}
                      loading="lazy"
                      className="w-full transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                    {img.caption && (
                      <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                        <p className="text-white text-xs font-sans">{img.caption}</p>
                      </div>
                    )}
                  </button>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <CinematicFooter contactEmail="synergymultimediaacademy@gmail.com" />
      <BackToTop visible={showBackToTop} />

      {lightbox !== null && <Lightbox images={images} startIndex={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  )
}
